<template>
  <div class="page">

    <!-- HEADER -->
    <div class="header sticky-header">
      <div>
        <h1>{{ $t('booking.titleSettings') }}</h1>
        <p>{{ $t('booking.subtitle') }}</p>
      </div>
      <button class="save-btn" @click="submitBooking" :disabled="loading">
        {{ loading ? $t('common.saving') : $t('common.save') }}
      </button>
    </div>

    <!-- TOAST -->
    <transition name="fade">
      <div v-if="toast.show" class="toast">
        {{ toast.message }}
      </div>
    </transition>

    <div class="grid">

      <!-- LEFT -->
      <div class="column">

        <!-- ENABLE -->
        <div class="card flex-between">
          <div>
            <h3>{{ $t('booking.enable') }}</h3>
            <p class="muted">{{ $t('booking.enableDesc') }}</p>
          </div>

          <label class="toggle">
            <input type="checkbox" v-model="booking.enabled" />
            <span class="slider"></span>
          </label>
        </div>

        <!-- SETTINGS -->
        <div class="card">
          <h3>{{ $t('booking.settings') }}</h3>

          <div class="grid-2">
            <div>
              <label>{{ $t('booking.timezone') }}</label>
              <select v-model="booking.timezone">
                <option v-for="tz in timezones" :key="tz" :value="tz">
                  {{ tz }}
                </option>
              </select>
            </div>

            <div>
              <label>{{ $t('booking.calendar') }}</label>
              <input v-model="booking.calendar_email" type="email" />
              <a :href="googleCalendarUrl" class="google-btn">
                Connect Google Calendar
              </a>
            </div>

            <div>
              <label>{{ $t('booking.duration') }}</label>
              <select v-model="booking.duration">
                <option :value="15">15 {{ $t('common.minutes') }}</option>
                <option :value="30">30 {{ $t('common.minutes') }}</option>
                <option :value="45">45 {{ $t('common.minutes') }}</option>
                <option :value="60">60 {{ $t('common.minutes') }}</option>
              </select>
            </div>

            <div>
              <label>{{ $t('booking.buffer') }}</label>
              <select v-model="booking.buffer">
                <option :value="0">0 {{ $t('common.minutes') }}</option>
                <option :value="5">5 {{ $t('common.minutes') }}</option>
                <option :value="10">10 {{ $t('common.minutes') }}</option>
                <option :value="15">15 {{ $t('common.minutes') }}</option>
              </select>
            </div>
          </div>
        </div>

        <!-- CONFIRMATION -->
        <div class="card">
          <h3>{{ $t('booking.confirmation') }}</h3>

          <div class="radio-card-group">

            <div 
              class="radio-card"
              :class="{ active: booking.confirmation_mode === 'auto' }"
              @click="booking.confirmation_mode = 'auto'"
            >
              <h4>{{ $t('booking.autoConfirm') }}</h4>
              <p>{{ $t('booking.autoConfirmDesc') }}</p>
            </div>

            <div 
              class="radio-card"
              :class="{ active: booking.confirmation_mode === 'manual' }"
              @click="booking.confirmation_mode = 'manual'"
            >
              <h4>{{ $t('booking.manualConfirm') }}</h4>
              <p>{{ $t('booking.manualConfirmDesc') }}</p>
            </div>

          </div>
        </div>

        <!-- AVAILABILITY -->
        <div class="card">
          <h3>{{ $t('booking.availability') }}</h3>

          <div class="availability">

            <div v-for="day in days" :key="day.key" class="day-card">

              <div class="day-header">
                <label class="toggle">
                  <input type="checkbox" v-model="day.enabled" />
                  <span class="slider"></span>
                </label>

                <span>{{ $t(`days.${day.key}`) }}</span>
              </div>

              <div v-if="day.enabled" class="time-row">
                <input type="time" v-model="day.start" />
                <span>→</span>
                <input type="time" v-model="day.end" />
              </div>

            </div>

          </div>
        </div>

      </div>

      <!-- RIGHT -->
      <div class="column">

        <!-- CONFIRMATION NOTIFICATIONS -->
        <div class="card">
          <h3>{{ $t('booking.confirmationNotifications') }}</h3>

          <div class="checkbox-card" @click="booking.confirm_email_enabled = !booking.confirm_email_enabled">
            <input type="checkbox" v-model="booking.confirm_email_enabled" />
            <span>{{ $t('common.email') }}</span>
          </div>

          <div class="checkbox-card" @click="booking.confirm_sms_enabled = !booking.confirm_sms_enabled">
            <input type="checkbox" v-model="booking.confirm_sms_enabled" />
            <span>SMS</span>
          </div>
        </div>

        <!-- REMINDERS -->
        <div class="card">
          <h3>{{ $t('booking.reminders') }}</h3>

          <div class="reminder">
            <input type="checkbox" v-model="booking.reminder_email_enabled" />
            <span>{{ $t('common.email') }}</span>
            <input type="number" v-model="booking.reminder_email_minutes" />
            <small>{{ $t('common.minutesBefore') }}</small>
          </div>

          <div class="reminder">
            <input type="checkbox" v-model="booking.reminder_sms_enabled" />
            <span>SMS</span>
            <input type="number" v-model="booking.reminder_sms_minutes" />
            <small>{{ $t('common.minutesBefore') }}</small>
          </div>
        </div>

        <!-- TEMPLATES -->
        <div class="card">
          <h3>{{ $t('booking.templates') }}</h3>

          <label>{{ $t('booking.emailTemplate') }}</label>
          <textarea v-model="booking.email_template" rows="3" />

          <label>{{ $t('booking.smsTemplate') }}</label>
          <textarea v-model="booking.sms_template" rows="2" />

          <label>{{ $t('booking.confirmEmailTemplate') }}</label>
          <textarea v-model="booking.confirm_email_template" rows="3" />

          <label>{{ $t('booking.confirmSmsTemplate') }}</label>
          <textarea v-model="booking.confirm_sms_template" rows="2" />
        </div>

        <!-- COLLECT -->
        <div class="card">
          <h3>{{ $t('booking.collect') }}</h3>

          <div class="checkboxes">
            <label><input type="checkbox" v-model="booking.collect_name" /> {{ $t('common.name') }}</label>
            <label><input type="checkbox" v-model="booking.collect_email" /> {{ $t('common.email') }}</label>
            <label><input type="checkbox" v-model="booking.collect_phone" /> {{ $t('common.phone') }}</label>
          </div>
        </div>

        <!-- AI -->
        <div class="card">
          <h3>{{ $t('booking.ai') }}</h3>
          <textarea v-model="booking.ai_instruction" rows="4" />
        </div>

      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, reactive, ref, onMounted } from 'vue'
import Cookies from 'js-cookie'
import { useRouter } from 'vue-router'
import * as api from '@/services/api.js'

const router = useRouter()

const toast = reactive({ show: false, message: '' })
const showToast = (msg) => {
  toast.message = msg
  toast.show = true
  setTimeout(() => (toast.show = false), 3000)
}

const loading = ref(false)
const agent_id = ref(Cookies.get('active_agent_id'))
const token = ref(Cookies.get('token'))
const apiUri = (process.env.VUE_APP_API_URL || 'https://api.nocodeagent.ai').replace(/\/$/, '')

const googleCalendarUrl = computed(() => {
  const params = new URLSearchParams({
    agent_id: agent_id.value || '',
    token: token.value || ''
  })

  return `${apiUri}/api/google/auth?${params.toString()}`
})

const booking = reactive({
  agent_id: null,
  enabled: true,
  timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  calendar_email: '',

  duration: 30,
  buffer: 5,

  confirmation_mode: 'auto',

  confirm_email_enabled: true,
  confirm_sms_enabled: false,
  confirm_email_template: '',
  confirm_sms_template: '',

  reminder_email_enabled: true,
  reminder_email_minutes: 60,
  reminder_sms_enabled: false,
  reminder_sms_minutes: 30,

  email_template: '',
  sms_template: '',

  collect_name: true,
  collect_email: true,
  collect_phone: false,

  ai_instruction: ''
})

const timezones = Intl.supportedValuesOf
  ? Intl.supportedValuesOf('timeZone')
  : ['UTC']

const days = reactive([
  { key: 'mon', enabled: true, start: '09:00', end: '17:00' },
  { key: 'tue', enabled: true, start: '09:00', end: '17:00' },
  { key: 'wed', enabled: true, start: '09:00', end: '17:00' },
  { key: 'thu', enabled: true, start: '09:00', end: '17:00' },
  { key: 'fri', enabled: true, start: '09:00', end: '17:00' },
  { key: 'sat', enabled: false, start: '10:00', end: '14:00' },
  { key: 'sun', enabled: false, start: '10:00', end: '14:00' }
])

onMounted(async () => {
  const agentId = Cookies.get('active_agent_id')
  if (!agentId) return router.push('/agents')

  booking.agent_id = agentId

  const res = await api.booking.getByAgent(agentId)
  if (!res) return

  Object.assign(booking, normalizeBookingSettings(res))

  if (res.availability) {
    try {
      const parsed = typeof res.availability === 'string'
        ? JSON.parse(res.availability)
        : res.availability

      if (Array.isArray(parsed)) {
        parsed.forEach(p => {
          const d = days.find(d => d.key === p.key)
          if (d) Object.assign(d, p)
        })
      }
    } catch (err) {
      showToast('Availability could not be loaded')
    }
  }
})

async function submitBooking() {
  try {
    loading.value = true

    await api.booking.save({
      ...normalizeBookingSettings(booking),
      availability: JSON.stringify(days)
    })

    showToast('Saved')

  } catch (err) {
    showToast('Error')
  } finally {
    loading.value = false
  }
}

function toBoolean(value, fallback = false) {
  if (value === undefined || value === null) return fallback
  return value === true || value === 1 || value === '1'
}

function normalizeBookingSettings(data) {
  return {
    ...data,
    enabled: toBoolean(data.enabled, true),
    duration: Number(data.duration || 30),
    buffer: Number(data.buffer || 0),
    confirm_email_enabled: toBoolean(data.confirm_email_enabled, true),
    confirm_sms_enabled: toBoolean(data.confirm_sms_enabled, false),
    reminder_email_enabled: toBoolean(data.reminder_email_enabled, true),
    reminder_email_minutes: Number(data.reminder_email_minutes || 60),
    reminder_sms_enabled: toBoolean(data.reminder_sms_enabled, false),
    reminder_sms_minutes: Number(data.reminder_sms_minutes || 30),
    collect_name: toBoolean(data.collect_name, true),
    collect_email: toBoolean(data.collect_email, true),
    collect_phone: toBoolean(data.collect_phone, false)
  }
}
</script>

<style scoped>
.page {
  padding: 30px;
  max-width: 1200px;
  margin: auto;
}

/* HEADER */
.header {
  display: flex;
  justify-content: space-between;
  margin-bottom: 25px;
}

/* GRID */
.grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
}

.column {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

/* CARD */
.card {
  background: white;
  border-radius: 16px;
  padding: 20px;
  border: 1px solid #e5e7eb;
  box-shadow: 0 4px 20px rgba(0,0,0,0.03);
}

/* INPUT */
input, select, textarea {
  width: 100%;
  padding: 10px;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
  background: #f9fafb;
  margin-top: 5px;
}

/* TOGGLE */
.toggle {
  position: relative;
  width: 50px;
  height: 28px;
}

.toggle input {
  display: none;
}

.slider {
  position: absolute;
  inset: 0;
  background: #e5e7eb;
  border-radius: 999px;
}

.slider::before {
  content: "";
  position: absolute;
  width: 22px;
  height: 22px;
  top: 3px;
  left: 3px;
  background: white;
  border-radius: 50%;
  transition: 0.3s;
}

input:checked + .slider {
  background: #6366f1;
}

input:checked + .slider::before {
  transform: translateX(22px);
}

/* RADIO CARD */
.radio-card-group {
  display: flex;
  gap: 10px;
}

.radio-card {
  flex: 1;
  padding: 15px;
  border-radius: 12px;
  border: 1px solid #e5e7eb;
  cursor: pointer;
}

.radio-card.active {
  border: 2px solid #6366f1;
  background: #eef2ff;
}

/* AVAILABILITY */
.availability {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.day-card {
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  padding: 10px;
}

.day-header {
  display: flex;
  align-items: center;
  gap: 10px;
}

.time-row {
  display: flex;
  gap: 10px;
  margin-top: 10px;
}

/* CHECKBOX */
.checkbox-card {
  display: flex;
  gap: 10px;
  padding: 10px;
  cursor: pointer;
}

.checkbox-card:hover {
  background: #f3f4f6;
}

/* BUTTON */
.save-btn {
  background: #6366f1;
  color: white;
  padding: 12px 18px;
  border-radius: 10px;
  border: none;
}

/* TOAST */
.toast {
  position: fixed;
  top: 20px;
  right: 20px;
  background: #22c55e;
  color: white;
  padding: 12px;
  border-radius: 8px;
}

.sticky-header {
  position: sticky;
  top: 0;
  z-index: 50;

  background: white;
  padding: 15px 0;

  border-bottom: 1px solid #e5e7eb;

  display: flex;
  justify-content: space-between;
  align-items: center;
}

.sticky-header {
  backdrop-filter: blur(10px);
  background: rgba(255,255,255,0.85);
}


.google-btn {
  background: #4285f4;
  color: white;
  padding: 10px 15px;
  border-radius: 8px;
  margin: 10px 0;
  display: inline-block;
  text-decoration: none;
}

</style>
